import { api } from './client';
import { Task, PriorityOption, TaskComment, TaskActivityEntry, TaskWorkflowStatus } from '../types/entities/task.entity';

/** Extra fields accepted by POST/PUT/PATCH /tasks. */
export interface TaskWorkflowPayload {
    workflow_status?: TaskWorkflowStatus;
    /** Actor key ("user:1", "bot:3") or null to unassign. */
    assignee?: string | null;
    /** Tag names. */
    tags?: string[];
}

interface TasksResponse {
    status: string;
    data: TaskData
}

export interface TaskData {
    tasks: Task[];
    meta: {
        total: number;
        currentPage: number;
        currentPageTotal: number;
        lastPage: number;
        perPage: number;
    };
}

export const taskService = {
    getAll: async (
        page: number = 1,
        perPage: number = 15,
        filters: string = '',
        sort: string = '',
        noteId?: string,
    ) => {
        let url = `/tasks?page=${page}&perPage=${perPage}`;
        if (noteId?.trim()) {
            url += `&note_id=${encodeURIComponent(noteId.trim())}`;
        }
        if (filters) {
            url += `&${filters}`;
        }
        if (sort) {
            url += `&${sort}`;
        }
        const response = await api.get<TasksResponse>(url);
        return response.data.data;
    },

    create: async (task: (Omit<Task, 'id' | 'created_at' | 'updated_at' | 'notes' | 'priority' | 'assignee' | 'tags' | 'created_by'> & TaskWorkflowPayload & { priority: number; note_ids?: string[]; project_ids?: string[] })) => {
        const response = await api.post<{ status: string; data: { task: Task } }>('/tasks', task);
        return response.data.data.task;
    },

    update: async (id: string, task: (Partial<Omit<Task, 'priority' | 'assignee' | 'tags' | 'created_by'>> & TaskWorkflowPayload & { priority?: number; note_ids?: string[]; project_ids?: string[] })) => {
        const response = await api.put<{ status: string; data: { task: Task } }>(`/tasks/${id}`, task);
        return response.data.data.task;
    },

    /** Partial update: only the sent fields change (workflow status, assignee, tags, ...). */
    patch: async (id: string, fields: TaskWorkflowPayload & { name?: string; status?: boolean; priority?: number }) => {
        const response = await api.patch<{ status: string; data: { task: Task } }>(`/tasks/${encodeURIComponent(id)}`, fields);
        return response.data.data.task;
    },

    getComments: async (taskId: string): Promise<TaskComment[]> => {
        const response = await api.get<{ data: { comments: TaskComment[] } }>(`/tasks/${encodeURIComponent(taskId)}/comments`);
        return response.data.data.comments ?? [];
    },

    addComment: async (taskId: string, body: string): Promise<TaskComment> => {
        const response = await api.post<{ data: { comment: TaskComment } }>(`/tasks/${encodeURIComponent(taskId)}/comments`, { body });
        return response.data.data.comment;
    },

    updateComment: async (taskId: string, commentId: string, body: string): Promise<TaskComment> => {
        const response = await api.put<{ data: { comment: TaskComment } }>(
            `/tasks/${encodeURIComponent(taskId)}/comments/${encodeURIComponent(commentId)}`,
            { body },
        );
        return response.data.data.comment;
    },

    deleteComment: async (taskId: string, commentId: string): Promise<void> => {
        await api.delete(`/tasks/${encodeURIComponent(taskId)}/comments/${encodeURIComponent(commentId)}`);
    },

    getActivity: async (taskId: string): Promise<TaskActivityEntry[]> => {
        const response = await api.get<{ data: { activity: TaskActivityEntry[] } }>(`/tasks/${encodeURIComponent(taskId)}/activity`);
        return response.data.data.activity ?? [];
    },

    delete: async (id: string) => {
        await api.delete(`/tasks/${id}`);
    },

    getEnums: async () => {
        const response = await api.get<{ data: { taskPriorities: PriorityOption[] } }>('/enums');
        return response.data.data.taskPriorities;
    },
};
